export const PAYMENT_PROOF_MAX_BYTES = 2 * 1024 * 1024
export const PAYMENT_PROOF_MAX_FILES = 5

export const PAYMENT_METHODS = [
  { value: 'BEFTN', label: 'BEFTN' },
  { value: 'RTGS', label: 'RTGS' },
  { value: 'NPSB', label: 'NPSB' },
  { value: 'CASH_DEPOSIT', label: 'Cash deposit' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'BKASH', label: 'bKash' },
]

const ACCOUNT_NUMBER_RE = /^\d{11,17}$/

/** Hard cap on a single proof amount (1 crore). Enforced in the amount field. */
export const MAX_PAYMENT_AMOUNT = 10_000_000
export const PAYMENT_PROOF_AMOUNT_MAX_MSG = 'Amount cannot be more than 10,000,000'
export const PAYMENT_AMOUNT_MUCH_LOWER_MSG =
  'Amount paid is {{difference}} tk less than the total amount. Confirm {{amount}} is correct.'
export const PAYMENT_AMOUNT_MUCH_HIGHER_MSG =
  'Amount paid is {{difference}} tk more than the total amount. Confirm {{amount}} is correct.'

export function isBkashPaymentMethod(method) {
  return String(method || '').toUpperCase() === 'BKASH'
}

export function isCashDepositPaymentMethod(method) {
  return String(method || '').toUpperCase() === 'CASH_DEPOSIT'
}

export function isChequePaymentMethod(method) {
  return String(method || '').toUpperCase() === 'CHEQUE'
}

export const BKASH_PHONE_RE = /^0\d{10}$/
export const BKASH_TRANSACTION_RE = /^[A-Za-z0-9]+$/
export const CHEQUE_NUMBER_MIN_LEN = 3
export const CHEQUE_NUMBER_MAX_LEN = 10
export const SAP_DOC_NO_RE = /^1\d{8}$/

export function requiresDepositorBankAccount(method) {
  const m = String(method || '').toUpperCase()
  return (
    PAYMENT_METHODS.some((row) => row.value === m) &&
    !isBkashPaymentMethod(m) &&
    !isCashDepositPaymentMethod(m) &&
    !isChequePaymentMethod(m)
  )
}

/** BEFTN, RTGS, and NPSB share depositor bank, branch, and account fields. */
export function sharesDepositorBankFields(method) {
  return requiresDepositorBankAccount(method)
}

/**
 * Field updates when the payment method changes. Keeps bank/branch/account when
 * switching within BEFTN/RTGS/NPSB; clears method-specific fields otherwise.
 */
export function buildPaymentMethodChangePatch(previousMethod, nextMethod) {
  const from = String(previousMethod || '').toUpperCase()
  const to = String(nextMethod || '').toUpperCase()
  const patch = {
    paymentMethod: nextMethod,
    savedAccountId: null,
    accountEntryMode: 'manual',
  }

  const keepDepositorBank =
    sharesDepositorBankFields(from) && sharesDepositorBankFields(to)

  if (!isBkashPaymentMethod(to)) {
    patch.bkashPhone = ''
    patch.bkashTransactionId = ''
  }
  if (!isChequePaymentMethod(to)) {
    patch.chequeNumber = ''
    patch.chequeDate = ''
  }

  if (!keepDepositorBank) {
    patch.accountNumber = ''
    if (!sharesDepositorBankFields(to)) {
      patch.bankId = null
      patch.bankName = ''
      Object.assign(patch, CLEARED_BRANCH_FIELDS)
    }
  }

  if (isBkashPaymentMethod(to)) {
    patch.bankId = null
    patch.bankName = ''
    Object.assign(patch, CLEARED_BRANCH_FIELDS)
    patch.accountNumber = ''
    if (!isBkashPaymentMethod(from)) {
      patch.bkashPhone = ''
      patch.bkashTransactionId = ''
    }
  }

  if (isCashDepositPaymentMethod(to) || isChequePaymentMethod(to)) {
    patch.saveAccount = false
  }

  return patch
}

/** Cash deposit and cheque proofs do not use saved grocer accounts or save-account. */
export function supportsSavedGrocerAccountPicker(method) {
  const m = String(method || '').trim()
  if (!m) return true
  if (isCashDepositPaymentMethod(m) || isChequePaymentMethod(m)) return false
  return requiresDepositorBankAccount(m) || isBkashPaymentMethod(m)
}

export function filterSavedAccountsForPaymentPicker(accounts, method) {
  const list = Array.isArray(accounts) ? accounts : []
  const m = String(method || '').trim().toUpperCase()
  if (isCashDepositPaymentMethod(m) || isChequePaymentMethod(m)) return []
  if (!m) {
    return list.filter(
      (row) =>
        !isCashDepositPaymentMethod(row?.payment_method) &&
        !isChequePaymentMethod(row?.payment_method),
    )
  }
  return list.filter(
    (row) => String(row?.payment_method || '').toUpperCase() === m,
  )
}

export function canSaveGrocerAccountForPaymentMethod(method) {
  return requiresDepositorBankAccount(method)
}

/**
 * Hydrates edit-rejected form state from an API proof row. Only fills fields that
 * apply to the proof's payment method (avoids copying account_number into bKash).
 */
export function pendingProofFromRejectedApiProof(api) {
  const base = createEmptyPendingProof()
  if (!api) return base
  const method = String(api.payment_method || '').toUpperCase()
  const at = api.payment_at ? new Date(api.payment_at) : null
  const pad = (n) => String(n).padStart(2, '0')
  const depositorBank = requiresDepositorBankAccount(method)

  let accountNumber = ''
  let bkashPhone = ''
  let bkashTransactionId = ''
  let chequeNumber = ''
  let chequeDate = ''
  if (isBkashPaymentMethod(method)) {
    bkashPhone = String(api.bkash_phone || api.account_number || '').trim()
    bkashTransactionId = api.bkash_transaction_id || ''
  } else if (isChequePaymentMethod(method)) {
    chequeNumber = api.cheque_number || ''
    chequeDate = api.cheque_date ? String(api.cheque_date).slice(0, 10) : ''
  } else if (depositorBank) {
    accountNumber = api.account_number || ''
  }

  return {
    ...base,
    paymentMethod: api.payment_method || '',
    bankId: depositorBank ? (api.bank_id ?? null) : null,
    branchId: depositorBank ? (api.branch_id ?? null) : null,
    bankName: depositorBank ? api.bank_name || '' : '',
    branchName: depositorBank ? api.branch_name || '' : '',
    routingNumber: depositorBank ? api.routing_number || '' : '',
    accountNumber,
    chequeNumber,
    chequeDate,
    bkashPhone,
    bkashTransactionId,
    paymentDate: at
      ? `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}`
      : '',
    paymentTime: at ? `${pad(at.getHours())}:${pad(at.getMinutes())}` : '',
    amount: api.requested_amount != null ? String(api.requested_amount) : '',
    shwapnoPaymentSettingsId: api.shwapno_payment_settings_id ?? null,
    file: { name: `Proof #${api.id}`, isExisting: true },
  }
}

export function createEmptyPendingProof() {
  return {
    file: null,
    paymentMethod: '',
    bankId: null,
    branchId: null,
    bkashPhone: '',
    bkashTransactionId: '',
    chequeNumber: '',
    chequeDate: '',
    // Snapshot of the selected bank/branch so labels render without the full list.
    bankName: '',
    branchName: '',
    routingNumber: '',
    accountNumber: '',
    paymentDate: '',
    paymentTime: '',
    amount: '',
    shwapnoPaymentSettingsId: null,
    saveAccount: true,
    savedAccountId: null,
    accountEntryMode: 'manual',
  }
}

export function isUsingManualGrocerAccount(proof) {
  if (!proof) return true
  return proof.accountEntryMode !== 'saved' || proof.savedAccountId == null
}

function listedBankForSavedAccount(account, banks) {
  const list = Array.isArray(banks) ? banks : []
  const byId = list.find((bank) => String(bank?.id) === String(account?.bank_id))
  if (byId) return byId
  const name = String(account?.bank_name || '').trim().toLowerCase()
  if (!name) return null
  return (
    list.find((bank) => String(bank?.name || '').trim().toLowerCase() === name) ||
    null
  )
}

/**
 * Patch for picking a saved shop account. Bank, branch, and account number
 * are filled from the saved row so the form matches the mini-shop picker.
 * An inactive branch keeps the bank and account and asks for a new branch.
 */
export function buildSavedAccountPatch(account, { banks } = {}) {
  if (!account) return {}
  const paymentMethod = account.payment_method || ''
  const listedBank = listedBankForSavedAccount(account, banks)
  const base = {
    paymentMethod,
    bankId: listedBank?.id ?? account.bank_id ?? null,
    bankName: listedBank?.name || account.bank_name || '',
    accountNumber: account.account_number || '',
    saveAccount: false,
    savedAccountId: account.id,
    accountEntryMode: 'saved',
  }
  if (isBkashPaymentMethod(paymentMethod)) {
    return {
      ...base,
      bankId: null,
      bankName: '',
      ...CLEARED_BRANCH_FIELDS,
    }
  }
  if (account.branch_is_active === false) {
    return {
      ...base,
      ...CLEARED_BRANCH_FIELDS,
      branchReselectRequired: true,
    }
  }
  return {
    ...base,
    branchId: account.branch_id ?? null,
    branchName: account.branch_name || '',
    routingNumber: account.routing_number || '',
    branchReselectRequired: false,
  }
}

/** Bank/branch fields cleared together (bKash, bank change). */
export const CLEARED_BRANCH_FIELDS = {
  branchId: null,
  branchName: '',
  routingNumber: '',
}

/** Client-side bank filter: name contains / code starts with the query. */
export function filterPaymentBanks(banks, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return banks || []
  return (banks || []).filter(
    (b) =>
      String(b?.name || '').toLowerCase().includes(q) ||
      String(b?.code || '').startsWith(q),
  )
}

/** Branch picker label is the branch name only. */
export function formatBranchOptionLabel(branch) {
  if (!branch) return ''
  return branch.name || ''
}

/**
 * Copies a previous proof's details (never file/date/time). No longer used when
 * adding a file: each new file starts with a blank set of payment details.
 */
export function copyDetailsFromPreviousProof(prev) {
  const next = createEmptyPendingProof()
  if (!prev) return next
  next.paymentMethod = prev.paymentMethod || ''
  next.bankId = prev.bankId ?? null
  next.branchId = prev.branchId ?? null
  next.bankName = prev.bankName || ''
  next.branchName = prev.branchName || ''
  next.routingNumber = prev.routingNumber || ''
  next.accountNumber = prev.accountNumber || ''
  next.shwapnoPaymentSettingsId = prev.shwapnoPaymentSettingsId ?? null
  return next
}

export function validatePaymentProofFile(file) {
  if (!file) return 'Please select at least one file to upload'
  /** Rejected-proof edit keeps the server file; no re-upload. */
  if (file.isExisting === true) return null
  const size = file.size ?? 0
  if (size > PAYMENT_PROOF_MAX_BYTES) {
    return 'File size must be less than 2MB'
  }
  const mime = String(file.type || '').toLowerCase()
  const name = String(file.name || '').toLowerCase()
  const ok =
    mime.startsWith('image/') ||
    mime.includes('pdf') ||
    name.endsWith('.pdf')
  if (!ok) return 'Only image or PDF files are allowed'
  return null
}

const PAYMENT_AMOUNT_RE = /^\d+(\.\d{1,2})?$/

/** Amount paid for a proof: a number > 0 with at most 2 decimals, else null. */
export function parsePaymentAmount(value) {
  if (value == null) return null
  const text = String(value).trim().replace(/,/g, '')
  if (!PAYMENT_AMOUNT_RE.test(text)) return null
  const n = Number(text)
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null
}

function amountInputExceedsMax(text) {
  if (!text || text === '.') return false
  const numeric = text.endsWith('.') ? text.slice(0, -1) : text
  if (!numeric) return false
  const n = Number(numeric)
  return !Number.isFinite(n) || n > MAX_PAYMENT_AMOUNT
}

/** Keeps only digits and one '.', with at most 2 decimals, capped at 1 crore. */
export function sanitizeAmountInput(value) {
  const cleaned = String(value ?? '').replace(/[^\d.]/g, '')
  const [whole, ...rest] = cleaned.split('.')
  const sanitized = rest.length === 0 ? whole : `${whole}.${rest.join('').slice(0, 2)}`
  if (amountInputExceedsMax(sanitized)) return String(MAX_PAYMENT_AMOUNT)
  return sanitized
}

function toPaisa(value) {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? Math.round(n * 100) : 0
}

export function formatTaka(value) {
  return `৳${(toPaisa(value) / 100).toFixed(2)}`
}

/** Grouped taka for warning copy, e.g. 47366.2 → "47,366.20", 50000 → "50,000". */
export function formatVarianceAmount(value) {
  const paisa = Math.abs(toPaisa(value))
  const whole = Math.floor(paisa / 100)
  const frac = paisa % 100
  const grouped = whole.toLocaleString('en-US')
  if (frac === 0) return grouped
  return `${grouped}.${String(frac).padStart(2, '0')}`
}

/** Sum of the valid amounts entered on the pending (new) proofs. */
export function getPendingProofsTotal(proofs) {
  const paisa = (proofs || [])
    .filter((p) => p?.file)
    .reduce((sum, p) => sum + toPaisa(parsePaymentAmount(p.amount) ?? 0), 0)
  return paisa / 100
}

/**
 * Amount still open for new proofs: order amount − verified − pending
 * (rejected excluded). Uses the server summary when available.
 */
export function getAvailablePaymentAmount(summary, fallbackPayable = 0) {
  if (summary?.available_to_submit != null) {
    return Math.max(0, Number(summary.available_to_submit) || 0)
  }
  const payable = Number(summary?.payable ?? fallbackPayable ?? 0) || 0
  const received = Number(summary?.received ?? 0) || 0
  const pending = Number(summary?.pending ?? 0) || 0
  return Math.max(0, (toPaisa(payable) - toPaisa(received) - toPaisa(pending)) / 100)
}

/**
 * Sum of amounts typed on proofs, including a proof that does not have a file yet.
 */
export function getEnteredPaymentTotal(proofs) {
  const paisa = (proofs || []).reduce((sum, p) => {
    const amount = parsePaymentAmount(p?.amount)
    return sum + (amount == null ? 0 : toPaisa(amount))
  }, 0)
  return paisa / 100
}

/** Designated remaining amount minus what has been typed on this upload. */
export function getLiveRemainingAmount(availableAmount, proofs) {
  if (availableAmount == null || availableAmount === '') return null
  const available = Number(availableAmount)
  if (!Number.isFinite(available)) return null
  return (toPaisa(available) - toPaisa(getEnteredPaymentTotal(proofs))) / 100
}

/**
 * Warn when the typed total is at least 50% below or 50% above the designated
 * remaining amount. The message states the taka difference. Overpayment inside
 * that band is allowed, and a larger gap still submits.
 */
export function getPaymentAmountVarianceWarning(proofs, designatedAmount) {
  if (designatedAmount == null || designatedAmount === '') return null
  const designated = Number(designatedAmount)
  if (!Number.isFinite(designated) || designated < 0) return null
  const enteredPaisa = toPaisa(getEnteredPaymentTotal(proofs))
  if (enteredPaisa <= 0) return null
  const designatedPaisa = toPaisa(designated)
  const low = Math.round(designatedPaisa * 0.5)
  const high = Math.round(designatedPaisa * 1.5)
  const difference = formatVarianceAmount((enteredPaisa - designatedPaisa) / 100)
  const amount = formatVarianceAmount(enteredPaisa / 100)
  if (designatedPaisa === 0 || enteredPaisa >= high) {
    return { message: PAYMENT_AMOUNT_MUCH_HIGHER_MSG, difference, amount }
  }
  if (enteredPaisa <= low) {
    return { message: PAYMENT_AMOUNT_MUCH_LOWER_MSG, difference, amount }
  }
  return null
}

const PAYMENT_DATE_YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const PAYMENT_TIME_HM_RE = /^(\d{2}):(\d{2})/

/** Browser-local calendar day as `YYYY-MM-DD` (never via toISOString/UTC). */
function toLocalYmd(date) {
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * A payment date is "in the future" only when its calendar day is after the
 * local today (compares `YYYY-MM-DD` strings). The old check compared the
 * selected day's 23:59:59 against now, which rejected today all day.
 */
export function isFuturePaymentDate(dateYmd, now = new Date()) {
  if (!dateYmd) return false
  if (!PAYMENT_DATE_YMD_RE.test(String(dateYmd))) return true
  return String(dateYmd) > toLocalYmd(now)
}

/** For today's date only: the time (HH:mm) is later than the current minute. */
export function isFuturePaymentTime(dateYmd, timeHm, now = new Date()) {
  if (!dateYmd || !timeHm) return false
  if (String(dateYmd) !== toLocalYmd(now)) return false
  const m = PAYMENT_TIME_HM_RE.exec(String(timeHm))
  if (!m) return false
  return Number(m[1]) * 60 + Number(m[2]) > now.getHours() * 60 + now.getMinutes()
}

function isShwapnoReceivingAccountSelected(proof, validShwapnoPaymentSettingsIds) {
  const id = proof?.shwapnoPaymentSettingsId
  if (!id) return false
  const allowed = validShwapnoPaymentSettingsIds
  if (!Array.isArray(allowed) || allowed.length === 0) return true
  return allowed.some((rowId) => String(rowId) === String(id))
}

export function validatePendingProof(
  proof,
  now = new Date(),
  { validShwapnoPaymentSettingsIds } = {},
) {
  const errors = {}
  if (!proof?.file) return errors
  const fileError = validatePaymentProofFile(proof.file)
  if (fileError) errors.file = fileError
  if (!proof.paymentMethod) errors.paymentMethod = 'Select a payment method'
  const method = String(proof.paymentMethod || '').toUpperCase()
  if (isBkashPaymentMethod(method)) {
    const phone = String(proof.bkashPhone || '').trim()
    if (!BKASH_PHONE_RE.test(phone)) {
      errors.bkashPhone = 'Phone must be 11 digits starting with 0'
    }
    const txn = String(proof.bkashTransactionId || '').trim()
    if (!txn || !BKASH_TRANSACTION_RE.test(txn)) {
      errors.bkashTransactionId = 'Transaction ID is required (alphanumeric)'
    }
  } else if (isChequePaymentMethod(method)) {
    const cheque = String(proof.chequeNumber || '').trim()
    if (
      cheque.length < CHEQUE_NUMBER_MIN_LEN ||
      cheque.length > CHEQUE_NUMBER_MAX_LEN
    ) {
      errors.chequeNumber = `Cheque number must be ${CHEQUE_NUMBER_MIN_LEN}–${CHEQUE_NUMBER_MAX_LEN} characters`
    }
    if (!proof.chequeDate) {
      errors.chequeDate = 'Cheque date is required'
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(String(proof.chequeDate))) {
      errors.chequeDate = 'Enter a valid cheque date'
    }
  } else if (requiresDepositorBankAccount(method)) {
    const digits = String(proof.accountNumber || '').replace(/\D/g, '')
    if (!digits) errors.accountNumber = 'Account number is required'
    else if (!ACCOUNT_NUMBER_RE.test(digits)) {
      errors.accountNumber = 'Account number must be 11–17 digits'
    }
    if (!proof.bankId) errors.bankId = 'Select a bank'
    if (!proof.branchId) errors.branchId = 'Select a branch'
  }
  if (!proof.paymentDate) errors.paymentDate = 'Payment date is required'
  else if (isFuturePaymentDate(proof.paymentDate, now)) {
    errors.paymentDate = 'Payment date cannot be in the future'
  }
  if (!proof.paymentTime) errors.paymentTime = 'Payment time is required'
  else if (!errors.paymentDate && isFuturePaymentTime(proof.paymentDate, proof.paymentTime, now)) {
    errors.paymentTime = 'Payment time cannot be in the future'
  }
  if (proof.amount == null || String(proof.amount).trim() === '') {
    errors.amount = 'Amount paid is required'
  } else if (parsePaymentAmount(proof.amount) == null) {
    errors.amount = 'Amount must be greater than 0 (max 2 decimals)'
  } else if (parsePaymentAmount(proof.amount) > MAX_PAYMENT_AMOUNT) {
    errors.amount = PAYMENT_PROOF_AMOUNT_MAX_MSG
  }
  if (
    !isShwapnoReceivingAccountSelected(proof, validShwapnoPaymentSettingsIds)
  ) {
    errors.shwapnoPaymentSettingsId = 'Select a Shwapno receiving account'
  }
  return errors
}

export function isPendingProofReady(proof, now = new Date(), options = {}) {
  return Object.keys(validatePendingProof(proof, now, options)).length === 0
}

export function countProofsWithFiles(proofs) {
  return (proofs || []).filter((p) => p?.file).length
}

export function getVisibleProofSlotCount(proofs) {
  const withFile = countProofsWithFiles(proofs)
  return Math.min(PAYMENT_PROOF_MAX_FILES, Math.max(1, withFile + 1))
}

/**
 * Sequential per-file entry: another file can be added only once every file
 * already added has complete, valid payment details, including payment date,
 * time and amount (and the max isn't hit).
 */
export function canAddAnotherProof(proofs, maxFiles = PAYMENT_PROOF_MAX_FILES) {
  const withFiles = (proofs || []).filter((p) => p?.file)
  if (withFiles.length >= maxFiles) return false
  return withFiles.every((p) => isPendingProofReady(p))
}

/** Every added file has complete payment details. Amount may exceed the order. */
export function canSubmitPendingProofs(proofs, now = new Date(), options = {}) {
  const withFiles = (proofs || []).filter((p) => p?.file)
  if (!withFiles.length) return false
  return withFiles.every((p) => isPendingProofReady(p, now, options))
}

/** Device's IANA timezone (e.g. "Asia/Kolkata"), or null if unavailable. */
export function getDeviceTimeZone() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return typeof zone === 'string' && zone ? zone : null
  } catch {
    return null
  }
}

/**
 * Device UTC offset in minutes east of UTC (e.g. 330 for UTC+05:30) at the
 * given local payment date/time, so DST is accounted for; falls back to now.
 */
export function getTimezoneOffsetMinutes(dateYmd, timeHm, now = new Date()) {
  const [y, m, d] = String(dateYmd || '').split('-').map((n) => Number(n))
  const [hh, mm] = String(timeHm || '').split(':').map((n) => Number(n))
  const at = new Date(y, m - 1, d, hh || 0, mm || 0)
  const offset = -(Number.isNaN(at.getTime()) ? now : at).getTimezoneOffset()
  return offset === 0 ? 0 : offset
}

/**
 * Timezone fields sent with each proof so the server reads payment_date /
 * payment_time in the uploader's timezone (server falls back to the offset,
 * then to Asia/Dhaka when neither is sent).
 */
export function buildPaymentTimezoneFields(proof) {
  return {
    payment_timezone: getDeviceTimeZone(),
    timezone_offset_minutes: getTimezoneOffsetMinutes(
      proof?.paymentDate,
      proof?.paymentTime,
    ),
  }
}

export function buildProofSubmitPayload(proof) {
  const method = String(proof.paymentMethod || '').toUpperCase()
  const bkash = isBkashPaymentMethod(method)
  const digits = requiresDepositorBankAccount(method)
    ? String(proof.accountNumber || '').replace(/\D/g, '')
    : bkash
      ? String(proof.bkashPhone || '').trim()
      : ''
  return {
    payment_method: method,
    bank_id: requiresDepositorBankAccount(method) ? proof.bankId : null,
    branch_id: requiresDepositorBankAccount(method) ? proof.branchId : null,
    account_number: digits,
    cheque_number: isChequePaymentMethod(method)
      ? String(proof.chequeNumber || '').trim()
      : null,
    cheque_date: isChequePaymentMethod(method)
      ? String(proof.chequeDate || '').trim()
      : null,
    bkash_phone: bkash ? String(proof.bkashPhone || '').trim() : null,
    bkash_transaction_id: bkash
      ? String(proof.bkashTransactionId || '').trim()
      : null,
    payment_date: proof.paymentDate,
    payment_time: proof.paymentTime,
    ...buildPaymentTimezoneFields(proof),
    amount: parsePaymentAmount(proof.amount),
    shwapno_payment_settings_id: proof.shwapnoPaymentSettingsId,
    saved_account_id:
      proof.accountEntryMode === 'saved' ? proof.savedAccountId ?? null : null,
    save_account:
      Boolean(proof.saveAccount) &&
      proof.accountEntryMode === 'manual' &&
      !proof.savedAccountId &&
      canSaveGrocerAccountForPaymentMethod(method),
  }
}

export function buildProofsPayloadFromPending(pendingProofs) {
  return (pendingProofs || [])
    .filter((p) => p?.file)
    .map((p) => buildProofSubmitPayload(p))
}

export function buildNewFilesFromPending(pendingProofs) {
  return (pendingProofs || [])
    .filter((p) => p?.file)
    .map((p) => p.file)
}

function formatVarianceTk(paisa) {
  const whole = Math.floor(Math.abs(paisa) / 100)
  const frac = Math.abs(paisa) % 100
  const grouped = whole.toLocaleString('en-US')
  if (frac === 0) return grouped
  return `${grouped}.${String(frac).padStart(2, '0')}`
}

/**
 * Finance verify dialog: SAP fields plus optional 50% amount variance warning (non-blocking).
 */
export function validatePaymentProofVerifyForm({
  receivedAmount,
  sapDocNo,
  requestedAmount,
  requireSapDocNo = true,
}) {
  const errors = {}
  const amount = parsePaymentAmount(receivedAmount)
  if (amount == null) {
    errors.receivedAmount = 'Enter a valid amount greater than 0 (max 2 decimals)'
  }
  const doc = String(sapDocNo || '').trim()
  if (requireSapDocNo && !SAP_DOC_NO_RE.test(doc)) {
    errors.sapDocNo = 'SAP document number must be 9 digits and start with 1'
  }

  let varianceWarning = null
  const designated = toPaisa(requestedAmount)
  const entered = toPaisa(amount)
  if (designated > 0 && entered > 0) {
    const low = Math.round(designated * 0.5)
    const high = Math.round(designated * 1.5)
    const diffTk = formatVarianceTk(entered - designated)
    const amountTk = formatVarianceTk(entered)
    if (entered >= high) {
      varianceWarning = `Amount paid is ${diffTk} tk more than the requested amount. Confirm ${amountTk} is correct.`
    } else if (entered <= low) {
      varianceWarning = `Amount paid is ${diffTk} tk less than the requested amount. Confirm ${amountTk} is correct.`
    }
  }

  return { errors, varianceWarning }
}
